import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Search, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: string[];
  onSelect: (suggestion: string) => void;
  onFetchSuggestions: (value: string) => void;
  onSubmit?: () => void;
  submitDisabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  rounded?: boolean;
  className?: string;
}

const DEBOUNCE_DELAY = 500;

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  className,
  suggestions,
  onChange,
  onSelect,
  onFetchSuggestions,
  onSubmit,
  submitDisabled = false,
  placeholder,
  rounded = true,
}) => {
  const { t } = useTranslation('common');
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState(value);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce input value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, DEBOUNCE_DELAY);
    return () => clearTimeout(handler);
  }, [value]);

  useEffect(() => {
    if (debouncedValue.length >= 3) {
      onFetchSuggestions(debouncedValue);
    }
  }, [debouncedValue, onFetchSuggestions]);

  // Control dropdown visibility: only show when we have suggestions
  useEffect(() => {
    if (debouncedValue.length >= 3) {
      // Only show dropdown if we have suggestions
      setShowDropdown(suggestions.length > 0);
    } else {
      setShowDropdown(false);
    }
    // Reset activeIndex when suggestions change
    setActiveIndex(-1);
  }, [suggestions.length, debouncedValue.length]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        onSelect(suggestions[activeIndex]);
        setShowDropdown(false);
      } else {
        setShowDropdown(false);
        if (onSubmit && !submitDisabled) {
          onSubmit();
        }
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSearchClick = () => {
    if (onSubmit && !submitDisabled) {
      onSubmit();
    }
    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} className={twMerge('relative w-full', className)}>
      <div className="flex items-center gap-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={e => {
              onChange(e.target.value);
              setActiveIndex(-1);
              if (e.target.value.length < 3) {
                setShowDropdown(false);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder || t('navigation.searchPlaceholder')}
            className={`w-full h-10 pl-4 pr-10 bg-white border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              rounded ? 'rounded-lg' : 'rounded-md'
            }`}
            autoComplete="off"
            onFocus={() => {
              if (value.length >= 3 && suggestions.length > 0) {
                setShowDropdown(true);
              }
            }}
          />

          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 transform items-center gap-1">
            {value && (
              <button
                type="button"
                onClick={() => {
                  onChange('');
                  setShowDropdown(false);
                  inputRef.current?.focus();
                }}
                className="rounded-full p-1 text-gray-400 hover:bg-gray-100"
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {!value && <Search className="pointer-events-none h-5 w-5 text-gray-400" />}
          </div>
        </div>

        {/* Search Button */}
        <button
          type="button"
          onClick={handleSearchClick}
          disabled={submitDisabled}
          className="h-10 flex-shrink-0 whitespace-nowrap rounded-full bg-primary px-6 font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('search.search', { defaultValue: 'Rechercher' })}
        </button>
      </div>

      {/* Suggestions Dropdown */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute left-0 right-0 top-12 z-20 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xl"
        >
          {suggestions.map((suggestion, idx) => (
            <div
              key={suggestion}
              className={`flex items-center justify-between px-4 py-2 cursor-pointer transition-colors ${
                idx === activeIndex
                  ? 'bg-gray-100 font-bold text-gray-900'
                  : 'hover:bg-gray-50 text-gray-700'
              }`}
              onMouseDown={() => {
                onSelect(suggestion);
                setShowDropdown(false);
              }}
              onMouseEnter={() => setActiveIndex(idx)}
            >
              <span className="text-sm leading-tight text-secondary">{suggestion}</span>
              <span className="ml-2 flex h-8 min-h-fit w-8 min-w-fit flex-shrink-0 items-center justify-center rounded-full border border-[#A3B2C2] bg-transparent hover:bg-gray-100">
                <Eye className="h-5 w-5 text-secondary" />
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
